/**
 * /dashboard/settings/onboarding-templates — Settings → People & HR →
 * Onboarding templates (SOP §8 + §11.9).
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/onboarding-templates/page.tsx
 * with SettingsShell + PageHeader folded into the leaf.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useOnboardingTemplates } from "@/hooks/use-hr";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { fmtDate } from "@/lib/hr-format";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { TemplateEditor } from "@/components/hr/template-editor";
import { AssignOnboardingDialog } from "@/components/hr/assign-onboarding-dialog";
import { TemplateDeactivateButton } from "@/components/hr/template-deactivate-button";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/onboarding-templates",
)({
  component: SettingsOnboardingTemplatesRoute,
});

function SettingsOnboardingTemplatesRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useOnboardingTemplates();

  const templates = useMemo(
    () =>
      [...(q.data?.templates ?? [])].sort(
        (a, b) =>
          Number(b.is_active) - Number(a.is_active) ||
          a.name.localeCompare(b.name),
      ),
    [q.data],
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Onboarding templates</h2>
              <p className="text-sm text-muted-foreground">
                Reusable onboarding checklists for your organisation.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isHrAdmin && (
                <RestoreDefaultsButton
                  catalog="onboarding-template"
                  catalogLabel="onboarding template"
                  invalidateKeys={[["hr", "templates"]]}
                />
              )}
              {isHrAdmin && <AssignOnboardingDialog />}
              {isHrAdmin && <TemplateEditor />}
            </div>
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can create or assign
                onboarding templates.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load templates</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && templates.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No onboarding templates yet.
                {isHrAdmin && " Use Restore defaults to seed the standard one."}
              </CardContent>
            </Card>
          )}

          {q.data && templates.length > 0 && (
            <div className="space-y-3">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base">{t.name}</CardTitle>
                        {t.description && (
                          <p className="mt-1 text-sm text-muted-foreground">
                            {t.description}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground">
                          Created {fmtDate(t.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge
                          status={t.is_active ? "valid" : "archived"}
                          label={t.is_active ? "Active" : "Inactive"}
                        />
                        {isHrAdmin && <TemplateEditor existing={t} />}
                        {isHrAdmin && t.is_active && (
                          <TemplateDeactivateButton template={t} />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
